import {
  Box,
  Button,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import type { Theme } from "@mui/material/styles";
import { openErrorNotification, openSuccessNotification } from "helpers/methods";
import React, { useState } from "react";
import { getRelativeFontSize, mediumFont, regularFont } from "utils/styles";
import { forgotPassword } from "./AuthServices";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

const textFieldSx = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      backgroundColor: isDark
        ? alpha(theme.palette.background.default, 0.5)
        : alpha(theme.palette.background.paper, 0.9),
      borderRadius: "12px",
      "& fieldset": {
        borderWidth: 1.5,
        borderColor: isDark
          ? alpha(theme.palette.divider, 0.5)
          : alpha(theme.palette.common.black, 0.22),
      },
      "&:hover fieldset": {
        borderColor: alpha(BRAND, 0.6),
      },
      "&.Mui-focused fieldset": {
        borderColor: BRAND,
        borderWidth: 2,
        boxShadow: `0 0 0 4px ${alpha(BRAND, 0.15)}`,
      },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      "&::placeholder": {
        fontSize: getRelativeFontSize(0),
        opacity: 0.8,
      },
    },
  };
};

interface ForgotPasswordProps {
  onBack?: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    if (!email.trim()) {
      setError("Email is required");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Invalid email address");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError("");

    try {
      const response = await forgotPassword(email.trim());
      if (response?.success !== false) {
        setSubmitted(true);
        openSuccessNotification(
          response?.message ?? "Reset link sent. Please check your email."
        );
      } else {
        openErrorNotification(
          response?.message ?? "Something went wrong. Please try again."
        );
      }
    } catch (err: any) {
      const msg =
        err?.message ?? err?.errorMessage ?? "Failed to send reset link.";
      setError(msg);
      openErrorNotification(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ mt: 0 }}>
        <Typography
          variant="body2"
          sx={{
            ...regularFont,
            fontSize: getRelativeFontSize(0),
            color: theme.palette.text.secondary,
            mb: 2,
          }}
        >
          If an account exists for {email}, you will receive a password reset
          link shortly.
        </Typography>
        {onBack && (
          <Button
            variant="text"
            onClick={onBack}
            sx={{
              ...mediumFont,
              fontSize: getRelativeFontSize(0),
              textTransform: "none",
              color: BRAND,
              "&:hover": { backgroundColor: alpha(BRAND, 0.08) },
            }}
          >
            Back to Sign in
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0 }} component="form" onSubmit={handleSubmit}>
      <TextField
        label="Email"
        type="email"
        variant="outlined"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setError("");
        }}
        error={!!error}
        helperText={error}
        fullWidth
        autoFocus
        placeholder="Enter your email address"
        sx={textFieldSx(theme)}
        InputLabelProps={{
          shrink: !!email,
          sx: { ...regularFont, fontSize: getRelativeFontSize(0), color: theme.palette.text.secondary },
        }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        onClick={() => handleSubmit()}
        sx={{
          ...mediumFont,
          minHeight: 48,
          py: 1.5,
          fontSize: getRelativeFontSize(1),
          textTransform: "none",
          borderRadius: "12px",
          background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
          boxShadow: `0 4px 20px ${alpha(BRAND, 0.35)}`,
          "&:hover": {
            background: `linear-gradient(135deg, ${BRAND_LIGHT} 0%, ${BRAND} 100%)`,
            boxShadow: `0 6px 24px ${alpha(BRAND, 0.45)}`,
          },
        }}
      >
        {loading ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          "Send reset link"
        )}
      </Button>
      {onBack && (
        <Button
          type="button"
          variant="text"
          fullWidth
          onClick={onBack}
          sx={{
            ...regularFont,
            fontSize: getRelativeFontSize(0),
            textTransform: "none",
            color: theme.palette.text.secondary,
            mt: 1.5,
            "&:hover": { color: BRAND, backgroundColor: alpha(BRAND, 0.06) },
          }}
        >
          Back to Sign in
        </Button>
      )}
    </Box>
  );
};

export default ForgotPassword;
